/**
 * AppShell V2 — Rail navigation + context drawer + status bar.
 *
 * Desktop: 48px icon rail (always) + optional context drawer (on hover/click)
 * Mobile: bottom tab bar (5 main items) + full drawer on hamburger
 */

import { useState, useEffect, useCallback, type ReactNode } from "react";
import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import {
  MessageSquare, Users, FolderGit2, Activity, Dumbbell, Timer,
  Code2, DollarSign, CheckSquare, BarChart3, Settings,
  Zap, Cpu, Plug, Bot, FileText, Shield, ScrollText, Bell, Gamepad2,
  Menu, X, Wifi, WifiOff, Search, LogOut, Layers, BookOpen, Radar,
} from "lucide-react";
import { ScanlineOverlay } from "../components/ui/ScanlineOverlay";
import { CommandPalette } from "../components/ui/CommandPalette";
import { useTheme } from "../hooks/useTheme";
import { useEventStreamStatus } from "../hooks/useEventStream";

interface AppShellProps {
  children: ReactNode;
}

// Navigation structure
interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  section: "main" | "system" | "personal";
}

const NAV_ITEMS: NavItem[] = [
  { to: "/chat", icon: <MessageSquare size={20} />, label: "Chat", section: "main" },
  { to: "/crew", icon: <Users size={20} />, label: "Crew", section: "main" },
  { to: "/projects", icon: <FolderGit2 size={20} />, label: "Projects", section: "main" },
  { to: "/editor", icon: <Code2 size={20} />, label: "Editor", section: "main" },
  { to: "/mcp", icon: <Plug size={18} />, label: "MCP", section: "main" },
  { to: "/subagents", icon: <Bot size={18} />, label: "Subagents", section: "main" },
  { to: "/context", icon: <FileText size={18} />, label: "Context", section: "main" },
  { to: "/skills", icon: <Cpu size={18} />, label: "Skills", section: "main" },
  { to: "/ops", icon: <Activity size={18} />, label: "Ops", section: "system" },
  { to: "/security", icon: <Shield size={18} />, label: "Security", section: "system" },
  { to: "/logs", icon: <ScrollText size={18} />, label: "Logs", section: "system" },
  { to: "/notifications", icon: <Bell size={18} />, label: "Notifs", section: "system" },
  { to: "/costs", icon: <DollarSign size={18} />, label: "Costos", section: "system" },
  { to: "/brief", icon: <BarChart3 size={18} />, label: "Brief", section: "personal" },
  { to: "/gym", icon: <Dumbbell size={18} />, label: "Gym", section: "personal" },
  { to: "/gym/today", icon: <Timer size={18} />, label: "Gym Hoy", section: "personal" },
  { to: "/gaming", icon: <Gamepad2 size={18} />, label: "Gaming", section: "personal" },
  { to: "/cards", icon: <Layers size={18} />, label: "Cards", section: "personal" },
  { to: "/habits", icon: <CheckSquare size={18} />, label: "Habits", section: "personal" },
  { to: "/learning", icon: <BookOpen size={18} />, label: "Learning", section: "personal" },
  { to: "/centinelas", icon: <Radar size={18} />, label: "Centinelas", section: "personal" },
  { to: "/settings", icon: <Settings size={18} />, label: "Settings", section: "personal" },
];

// Mobile bottom bar — top 5 most used
const MOBILE_TAB_ITEMS = ["/chat", "/projects", "/ops", "/gym/today", "/brief"];

function RailItem({ item, isActive }: { item: NavItem; isActive: boolean }) {
  return (
    <Link
      to={item.to}
      className={`
        relative w-10 h-10 flex items-center justify-center rounded-sm
        transition-all duration-[var(--bw-transition-fast)]
        group
        ${isActive
          ? "text-[var(--bw-accent)] bg-[var(--bw-accent)]/10"
          : "text-[var(--bw-muted)] hover:text-[var(--bw-fg)] hover:bg-[var(--bw-glass-bg)]"
        }
      `}
    >
      {/* Active indicator */}
      {isActive && (
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-5 rounded-r"
          style={{ backgroundColor: "var(--bw-accent)", boxShadow: "0 0 8px var(--bw-accent)" }}
        />
      )}
      {item.icon}

      {/* Tooltip */}
      <div className="
        absolute left-full ml-3 px-2 py-1
        bg-[var(--bw-bg)] border border-[var(--bw-glass-border)]
        text-[10px] text-[var(--bw-fg)] font-mono uppercase tracking-wider
        whitespace-nowrap
        opacity-0 group-hover:opacity-100
        pointer-events-none
        transition-opacity duration-150
        z-50
      "
        style={{ boxShadow: "var(--bw-elevation-2)" }}
      >
        {item.label}
      </div>
    </Link>
  );
}

async function handleLogout() {
  await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
  window.location.href = "/login";
}

export function AppShell({ children }: AppShellProps) {
  const { theme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isConnected } = useEventStreamStatus();
  const navigate = useNavigate();

  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [pathname]);

  // Current time for status bar
  const [time, setTime] = useState(new Date().toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" }));
  useEffect(() => {
    const t = setInterval(() => setTime(new Date().toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })), 30000);
    return () => clearInterval(t);
  }, []);

  const mainItems = NAV_ITEMS.filter((i) => i.section === "main");
  const systemItems = NAV_ITEMS.filter((i) => i.section === "system");
  const personalItems = NAV_ITEMS.filter((i) => i.section === "personal");

  return (
    <>
      <CommandPalette />

      <div className="relative flex h-[100dvh] w-screen overflow-hidden bg-[var(--bw-bg)] text-[var(--bw-fg)] font-mono">
        {/* Background layers */}
        {theme.isDark && (
          <>
            <div className="fixed inset-0 pointer-events-none z-0" style={{ background: "var(--bw-ambient-glow)" }} />
            <div
              className="fixed inset-0 pointer-events-none z-0"
              style={{
                backgroundImage: `linear-gradient(var(--bw-grid-color) 1px, transparent 1px), linear-gradient(90deg, var(--bw-grid-color) 1px, transparent 1px)`,
                backgroundSize: "var(--bw-grid-size) var(--bw-grid-size)",
                animation: "bw-neon-pulse 8s ease-in-out infinite",
              }}
            />
            <ScanlineOverlay intensity="low" />
          </>
        )}

        {/* ═══ DESKTOP: Icon Rail ═══ */}
        <nav
          className="hidden md:flex flex-col items-center w-[52px] py-3 gap-1 z-10 flex-shrink-0"
          style={{
            background: "var(--bw-glass-bg)",
            backdropFilter: "blur(16px)",
            borderRight: "1px solid var(--bw-glass-border)",
          }}
        >
          {/* Logo */}
          <Link to="/brief" className="mb-3 w-8 h-8 grid place-items-center border border-[var(--bw-accent)]/40 hover:border-[var(--bw-accent)] transition-colors" style={{ boxShadow: "var(--bw-glow)" }}>
            <Zap size={16} className="text-[var(--bw-accent)]" />
          </Link>

          {/* Main section */}
          {mainItems.map((item) => (
            <RailItem key={item.to} item={item} isActive={pathname === item.to || pathname.startsWith(item.to + "/")} />
          ))}

          <div className="w-6 border-t border-[var(--bw-glass-border)] my-2" />

          {/* System section */}
          {systemItems.map((item) => (
            <RailItem key={item.to} item={item} isActive={pathname === item.to} />
          ))}

          <div className="w-6 border-t border-[var(--bw-glass-border)] my-2" />

          {/* Personal section */}
          <div className="mt-auto flex flex-col items-center gap-1">
            {personalItems.map((item) => (
              <RailItem key={item.to} item={item} isActive={pathname === item.to} />
            ))}
          </div>

          {/* Connection status */}
          <div className="mt-2 mb-1">
            <div
              className="w-2 h-2 rounded-full"
              style={{
                backgroundColor: isConnected ? "var(--bw-success)" : "var(--bw-muted)",
                boxShadow: isConnected ? "0 0 6px var(--bw-success)" : "none",
              }}
              title={isConnected ? "Connected" : "Offline"}
            />
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            title="Logout"
            className="w-10 h-10 flex items-center justify-center rounded-sm text-[var(--bw-muted)] hover:text-red-400 hover:bg-red-400/10 transition-all duration-[var(--bw-transition-fast)] group relative mb-1"
          >
            <LogOut size={16} />
            <div className="absolute left-full ml-3 px-2 py-1 bg-[var(--bw-bg)] border border-[var(--bw-glass-border)] text-[10px] text-[var(--bw-fg)] font-mono uppercase tracking-wider whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50" style={{ boxShadow: "var(--bw-elevation-2)" }}>
              Logout
            </div>
          </button>
        </nav>

        {/* ═══ MAIN CONTENT ═══ */}
        <main className="flex-1 min-w-0 overflow-hidden flex flex-col z-10">
          {/* ─── Status bar (desktop) ─── */}
          <div
            className="hidden md:flex items-center gap-4 px-4 h-8 text-[10px] font-mono uppercase tracking-wider flex-shrink-0"
            style={{
              background: "var(--bw-glass-bg)",
              backdropFilter: "blur(8px)",
              borderBottom: "1px solid var(--bw-glass-border)",
            }}
          >
            <span className="text-[var(--bw-accent)] font-bold">BLACK WALL</span>
            <span className="text-[var(--bw-muted)]">v2</span>
            <div className="flex-1" />
            <button
              onClick={() => navigate({ to: "/brief" })}
              className="text-[var(--bw-muted)] hover:text-[var(--bw-fg)] transition-colors"
            >
              {isConnected ? "● LIVE" : "○ OFFLINE"}
            </button>
            <span className="text-[var(--bw-muted)]">|</span>
            <button
              onClick={() => {
                const e = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
                window.dispatchEvent(e);
              }}
              className="flex items-center gap-1 text-[var(--bw-muted)] hover:text-[var(--bw-fg)] transition-colors"
            >
              <Search size={10} /> Cmd+K
            </button>
            <span className="text-[var(--bw-muted)]">|</span>
            <span className="text-[var(--bw-fg)]">{time}</span>
          </div>

          {/* ─── Mobile top bar ─── */}
          <header
            className="md:hidden flex items-center gap-3 px-4 py-3 flex-shrink-0"
            style={{
              background: "var(--bw-glass-bg)",
              backdropFilter: "blur(12px)",
              borderBottom: "1px solid var(--bw-glass-border)",
            }}
          >
            <button onClick={() => setMobileMenuOpen(true)} className="p-1 text-[var(--bw-fg)] hover:text-[var(--bw-accent)]">
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-[var(--bw-accent)]" />
              <span className="text-xs font-bold uppercase tracking-wider">Black Wall</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: isConnected ? "var(--bw-success)" : "var(--bw-muted)" }} />
              <span className="text-[10px] text-[var(--bw-muted)]">{time}</span>
            </div>
          </header>

          {/* ─── Page content ─── */}
          <div className="flex-1 min-h-0 overflow-auto overflow-x-hidden">
            {children}
          </div>

          {/* ─── Mobile bottom tab bar ─── */}
          <nav
            className="md:hidden flex items-center justify-around py-2 flex-shrink-0"
            style={{
              background: "var(--bw-glass-bg)",
              backdropFilter: "blur(12px)",
              borderTop: "1px solid var(--bw-glass-border)",
            }}
          >
            {MOBILE_TAB_ITEMS.map((path) => {
              const item = NAV_ITEMS.find((n) => n.to === path)!;
              const active = pathname === path || pathname.startsWith(path + "/");
              return (
                <Link
                  key={path}
                  to={path}
                  className={`flex flex-col items-center gap-0.5 px-3 py-1 transition-colors ${
                    active ? "text-[var(--bw-accent)]" : "text-[var(--bw-muted)]"
                  }`}
                >
                  {item.icon}
                  <span className="text-[8px] uppercase tracking-wider">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </main>

        {/* ═══ MOBILE FULL DRAWER ═══ */}
        {mobileMenuOpen && (
          <>
            <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
            <div
              className="fixed inset-y-0 left-0 z-50 w-64 flex flex-col py-4 overflow-y-auto"
              style={{
                background: "var(--bw-glass-bg)",
                backdropFilter: "blur(20px)",
                borderRight: "1px solid var(--bw-glass-border)",
                boxShadow: "var(--bw-elevation-3)",
              }}
            >
              <div className="flex items-center justify-between px-4 mb-4">
                <div className="flex items-center gap-2">
                  <Zap size={16} className="text-[var(--bw-accent)]" />
                  <span className="text-sm font-bold uppercase tracking-wider">Black Wall</span>
                </div>
                <button onClick={() => setMobileMenuOpen(false)} className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-fg)]">
                  <X size={18} />
                </button>
              </div>

              {[
                { label: "MAIN", items: mainItems },
                { label: "SYSTEM", items: systemItems },
                { label: "PERSONAL", items: personalItems },
              ].map((section) => (
                <div key={section.label} className="mb-3">
                  <p className="px-4 text-[9px] text-[var(--bw-muted)] uppercase tracking-[0.2em] mb-1">{section.label}</p>
                  {section.items.map((item) => {
                    const active = pathname === item.to;
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                          active ? "text-[var(--bw-accent)] bg-[var(--bw-accent)]/5" : "text-[var(--bw-muted)] hover:text-[var(--bw-fg)]"
                        }`}
                      >
                        {item.icon}
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              ))}

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-wider text-[var(--bw-muted)] hover:text-red-400 transition-colors w-full border-t border-[var(--bw-glass-border)] mt-2 pt-4"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
